import React, { useMemo } from 'react'
import PropTypes from 'prop-types'
import { Table, Typography, Tag, Checkbox, Button, Space, Row } from 'antd'
import * as Icons from '@ant-design/icons'
import { Link, generatePath } from 'react-router-dom'
import _ from 'lodash'

import * as ROUTES from 'admin/routes'
import { Queries } from 'admin/lib/Api'
import { CertificateTag } from 'admin/lib/Certificates/CertificateTag'
import { ScreenSpin } from 'admin/lib/ScreenSpin'

export function ReferenceProductItemsList({
  searchTerm = ''
}) {
  const referenceProductItems = Queries.useReferenceProductItems(searchTerm)
  const allRPIs = useMemo(() => {
    if (!referenceProductItems.data?.pages?.length) {
      return []
    }

    return _.flatMap(referenceProductItems.data.pages, page => page.items)
  }, [referenceProductItems.data?.pages])

  const companies = Queries.useCompanies()
  const certificates = Queries.useCertificates()

  const companiesById = useMemo(() => {
    if (!companies.isFetched) {
      return {}
    }

    return _.keyBy(companies.data, company => company.companyId)
  }, [companies.data, companies.isFetched])

  const certificatesById = useMemo(() => {
    if (!certificates.isFetched) {
      return {}
    }

    return _.keyBy(certificates.data, certificate => certificate.certificateId)
  }, [certificates.data, certificates.isFetched])

  const columns = useMemo(
    () => [
      {
        title: 'Reference Product Item',
        dataIndex: 'name',
        sorter: (a, b) => a.name.localeCompare(b.name)
      },
      {
        title: 'Certificates',
        dataIndex: 'certificateIds',
        render: certificateIds => {
          if (!certificates.isFetched) {
            return <Icons.LoadingOutlined />
          } else {
            return certificateIds.map(id => {
              const cert = certificatesById[id]
              return <CertificateTag key={id} cert={cert} />
            })
          }
        }
      },
      {
        title: 'Companies',
        dataIndex: 'companyIds',
        render: companyIds => {
          if (!companies.isFetched) {
            return <Icons.LoadingOutlined />
          } else {
            return companyIds.map(id => {
              const company = companiesById[id]
              return (
                <Tag key={id} color={company ? 'default' : 'red'}>
                  {company ? company.name : 'Unknown Company'}
                </Tag>
              )
            })
          }
        }
      },
      {
        title: '',
        key: 'edit',
        width: '4rem',
        render: referenceItem => (
          <Typography.Text key={referenceItem.referenceProductId}>
            <Link
              to={generatePath(ROUTES.REFERENCEITEMS.EDIT, {
                referenceItemId: referenceItem.referenceProductId
              })}
            >
              Edit
            </Link>
          </Typography.Text>
        )
      }
    ],
    [certificates.isFetched, certificatesById, companies.isFetched, companiesById]
  )

  if (!referenceProductItems.isFetched) {
    return <ScreenSpin />
  }

  return (
    <Space direction="vertical" style={{ width: '100%', marginBottom: '2rem' }}>
      <Table
        columns={columns}
        dataSource={allRPIs}
        rowKey="referenceProductId"
        pagination={false}
        style={{ width: '100%', height: 'auto' }}
      />

      <Row justify="center">
        <Button
          type="primary"
          onClick={() => referenceProductItems.fetchNextPage()}
          disabled={
            referenceProductItems.isFetchingNextPage ||
            !referenceProductItems.hasNextPage
          }
          size="large"
          style={{ width: '15rem' }}
          icon={
            referenceProductItems.isFetchingNextPage ? (
              <Icons.LoadingOutlined />
            ) : (
              <Icons.ArrowDownOutlined />
            )
          }
        >
          {referenceProductItems.isFetchingNextPage
            ? 'Loading...'
            : 'Load More'}
        </Button>
      </Row>
    </Space>
  )
}

ReferenceProductItemsList.propTypes = {
  searchTerm: PropTypes.string
}
